import { criterion, fail, fromCriteria } from "@/core/check";
import type { CheckResult, Lesson } from "@/core/types";
import { type AbfrageInfo, readAbfragen } from "@/office/excel/analyse";
import { nichtPruefbar } from "../schnittstelle";

async function mitAbfragen(bewerten: (abfragen: AbfrageInfo[]) => CheckResult): Promise<CheckResult> {
  const abfragen = await readAbfragen();
  if (abfragen === null) {
    return nichtPruefbar(
      "Die Power-Query-Abfragen",
      "1.14",
      "Öffne Daten → Abfragen und Verbindungen und sieh nach, ob deine Abfrage dort steht.",
      true,
    );
  }
  if (abfragen.length === 0) {
    return fail(
      "Die Arbeitsmappe enthält noch keine Power-Query-Abfrage.",
      "Daten → Aus Tabelle/Bereich startet den Power-Query-Editor mit den markierten Daten.",
    );
  }
  return bewerten(abfragen);
}

export const lektionPowerQuery: Lesson = {
  id: "power-query",
  title: "Daten abrufen mit Power Query",
  summary: "Aufbereitungsschritte einmal festlegen – und bei neuen Daten nur noch aktualisieren.",
  durationMinutes: 14,
  learningGoals: [
    "Daten über „Daten abrufen“ oder „Aus Tabelle/Bereich“ in Power Query laden",
    "Im Power-Query-Editor Schritte wie Spalte teilen, Typ ändern und Filtern anwenden",
    "Das Ergebnis als Tabelle laden und später aktualisieren",
  ],
  steps: [
    {
      id: "abfrage-anlegen",
      title: "Eine Abfrage anlegen",
      explanation: [
        "Power Query ist das Werkzeug für wiederkehrende Datenaufbereitung. „Daten“ → " +
          "„Daten abrufen“ liest Dateien, Ordner, Datenbanken und Webseiten; „Aus " +
          "Tabelle/Bereich“ nimmt Daten, die schon in der Mappe stehen.",
        "Der Power-Query-Editor zeigt die Daten und rechts die „Angewendeten Schritte“. " +
          "Jede Aktion – Spalte teilen, Datentyp ändern, Zeilen filtern, Duplikate " +
          "entfernen – wird dort als Schritt gespeichert, nicht als Ergebnis. Genau das ist " +
          "der Unterschied zu allem, was du bisher von Hand gemacht hast.",
        "„Schließen & laden“ schreibt das Ergebnis als neue Tabelle auf ein eigenes Blatt.",
      ],
      keyPoints: [
        "Daten → Daten abrufen oder Aus Tabelle/Bereich.",
        "Jede Aktion wird ein Schritt – die Schritte sind das Rezept.",
        "Schließen & laden → Ergebnis als Tabelle.",
      ],
      task: {
        instruction:
          "Mach aus einer Liste eine Tabelle, lade sie mit „Aus Tabelle/Bereich“ in Power " +
          "Query, ändere dort mindestens einen Datentyp und lade das Ergebnis als Tabelle.",
        hint: "Daten → Aus Tabelle/Bereich, im Editor Spaltenkopf → Datentyp, dann Start → Schließen & laden.",
        check: () =>
          mitAbfragen((abfragen) => {
            const geladen = abfragen.filter((abfrage) => abfrage.loadedTo === "Table");
            return fromCriteria(
              [
                criterion(`Abfragen in der Mappe: ${abfragen.map((a) => a.name).join(", ")}`, true),
                criterion(
                  geladen[0]
                    ? `„${geladen[0].name}“ ist als Tabelle geladen (${geladen[0].rowsLoadedCount ?? "?"} Zeilen)`
                    : "Eine Abfrage ist als Tabelle geladen",
                  geladen.length > 0,
                ),
              ],
              {
                success: "Die Abfrage steht – ab jetzt ist Aufbereiten ein Klick auf „Aktualisieren“.",
                failure: "Die Abfrage ist noch nicht als Tabelle geladen.",
                hint: "„Nur Verbindung erstellen“ lädt nichts auf ein Blatt – wähle „Tabelle“.",
              },
            );
          }),
      },
    },
    {
      id: "aktualisieren",
      title: "Aktualisieren statt neu aufbereiten",
      explanation: [
        "Ändern sich die Quelldaten – eine neue Zeile in der Ausgangstabelle, eine neue " +
          "Exportdatei unter demselben Namen –, genügt „Daten“ → „Alle aktualisieren“ " +
          "(Strg + Alt + F5). Power Query spielt alle Schritte erneut ab.",
        "Im Bereich „Abfragen und Verbindungen“ zeigt jede Abfrage, wann sie zuletzt " +
          "aktualisiert wurde und wie viele Zeilen sie geladen hat. Eine rote Zahl weist auf " +
          "Fehler hin, etwa wenn eine Spalte in der Quelle umbenannt wurde.",
        "Für große Datenmengen lässt sich das Ergebnis statt auf ein Blatt ins Datenmodell " +
          "laden. PivotTables können dann mehrere Tabellen über Beziehungen verbinden – ein " +
          "Thema für sich, das hier nur erwähnt sei.",
      ],
      keyPoints: [
        "Daten → Alle aktualisieren (Strg + Alt + F5).",
        "Abfragen und Verbindungen zeigt Zeilenzahl und Fehler.",
        "Datenmodell für große Datenmengen und Beziehungen.",
      ],
      task: {
        instruction:
          "Füge der Ausgangstabelle eine Zeile hinzu und aktualisiere die Abfrage, sodass " +
          "die neue Zeile im Ergebnis erscheint.",
        hint: "Neue Zeile unter die Ausgangstabelle schreiben, dann Daten → Alle aktualisieren.",
        check: () =>
          mitAbfragen((abfragen) => {
            const aktualisiert = abfragen.filter((abfrage) => abfrage.refreshDate !== null);
            const fehlerfrei = abfragen.filter((abfrage) => abfrage.error === null || abfrage.error === "None");
            return fromCriteria(
              [
                criterion(
                  aktualisiert[0]?.refreshDate
                    ? `Zuletzt aktualisiert: ${aktualisiert[0].refreshDate.toLocaleString("de-DE")}`
                    : "Die Abfrage wurde aktualisiert",
                  aktualisiert.length > 0,
                ),
                criterion("Die letzte Aktualisierung war fehlerfrei", fehlerfrei.length === abfragen.length),
              ],
              {
                success: "Aktualisiert – die neue Zeile ist ohne einen einzigen Handgriff im Ergebnis.",
                failure: "Die Abfrage wurde noch nicht fehlerfrei aktualisiert.",
              },
            );
          }),
      },
    },
  ],
};
